// The shop's side of Plans & pricing and the vendor control centre (D-065, owner 2026-10-25):
//  - plan locks: a feature the shop's plan turns off shows, in the sidebar and in place of its page,
//    the icon of the plan that has it (the icon the owner gave the plan in the console), with Upgrade;
//  - banners: a support-access request to allow or deny, live support access, the support view's own
//    read-only banner, a suspended shop's reason, and pinned messages from Adyvance;
//  - "From Adyvance" messages in the bell;
//  - D-067: What's new (unseen notes count on the bell and link to Help centre → What's new).
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  featureForNav,
  PLAN_FEATURES,
  SWITCHES,
  type PlanUnlock,
  type ShopNotice,
  type ShopWhatsNew,
  type ShopSupportGrant,
} from "@ady/contracts";
import { formatDate, formatMoney, formatTime } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { Banner, Button, cx, Icon, useToast } from "@ady/ui";
import { api, request } from "../api/client";
import { errorMessage } from "../api/errors";
import { useEntitlements } from "../api/queries";
import { CheckoutPopup } from "../billing/WalletPopups";
import { useWalletPlans } from "../billing/walletApi";
import { AppLink } from "./AppLink";
import { useShell } from "./context";
import { can, itemForPathname, type NavItem } from "./nav";

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

export const shopControlApi = {
  notices: (orgId: string) => request<{ data: ShopNotice[] }>("GET", `${org(orgId)}/notices`),
  seen: (orgId: string, noticeId: string, dismiss: boolean) =>
    request<{ ok: true }>("POST", `${org(orgId)}/notices/${encodeURIComponent(noticeId)}/seen`, {
      body: dismiss ? { dismiss: true } : {},
    }),
  grants: (orgId: string) =>
    request<{ data: ShopSupportGrant[] }>("GET", `${org(orgId)}/support-access`),
  decide: (orgId: string, grantId: string, decision: "approve" | "deny") =>
    request<{ ok: true }>(
      "POST",
      `${org(orgId)}/support-access/${encodeURIComponent(grantId)}/decision`,
      { body: { decision } },
    ),
  end: (orgId: string, grantId: string) =>
    request<{ ok: true }>(
      "POST",
      `${org(orgId)}/support-access/${encodeURIComponent(grantId)}/end`,
    ),
};
const keys = {
  whatsNew: (orgId: string) => ["settings", orgId, "whats-new"] as const,
  notices: (orgId: string) => ["org", orgId, "notices"] as const,
  grants: (orgId: string) => ["org", orgId, "supportAccess"] as const,
};

export const featureLabel = (key: string) =>
  i18n.tDynamic(`plans.features.${key}`, "billing.quotas.unknown");

// ---------- plan locks ----------

/** The feature a nav item needs and the plan that has it, when the shop's plan turns it off. */
export function usePlanLocks() {
  const { membership } = useShell();
  const ent = useEntitlements(membership.org.id);
  return useMemo(() => {
    const locks = ent.data?.locks ?? {};
    return (navId: string): { feature: string; unlock: PlanUnlock | null } | null => {
      const f = featureForNav(navId);
      if (!f || !(f.key in locks)) return null;
      return { feature: f.key, unlock: locks[f.key] ?? null };
    };
  }, [ent.data]);
}

/** The plan that has a feature, when the shop's plan turns it off (null when the shop has it). */
export function useFeatureLock() {
  const { membership } = useShell();
  const ent = useEntitlements(membership.org.id);
  return useMemo(() => {
    const locks = ent.data?.locks ?? {};
    return (key: string | null): { unlock: PlanUnlock | null } | null =>
      key && key in locks ? { unlock: locks[key] ?? null } : null;
  }, [ent.data]);
}

/** What's new for this member (D-067); not during a support view. */
export function useWhatsNew() {
  const { me, membership } = useShell();
  const orgId = membership.org.id;
  return useQuery({
    queryKey: keys.whatsNew(orgId),
    queryFn: () => request<ShopWhatsNew>("GET", `${org(orgId)}/whats-new`),
    enabled: !me.support,
    refetchInterval: 300_000,
  });
}

/** The bell's What's new line: "2 new in What's new" → Help centre. */
export function WhatsNewBell({ onOpen }: { onOpen?: () => void }) {
  const { orgSlug, storeSlug } = useShell();
  const q = useWhatsNew();
  const unseen = q.data?.unseen ?? 0;
  if (!unseen) return null;
  return (
    <AppLink
      href={`/${orgSlug}/${storeSlug}/settings/help?tab=whats-new`}
      onClick={onOpen}
      className="ady-bg-brand-tint flex items-center gap-2 rounded-sm px-2 py-1 text-fg no-underline"
      data-testid="whats-new-bell"
    >
      <Icon name="sparkle" gradient />
      <span className="flex-1 text-xs font-semibold">{t("desk.shop.whatsNew.bell")}</span>
      <span className="rounded-full bg-danger-bg px-1.5 text-2xs font-semibold text-danger-fg">
        {t("desk.shop.whatsNew.count", { n: unseen })}
      </span>
    </AppLink>
  );
}

/** The small plan icon a locked sidebar row carries ("In Growth"). */
export function PlanLockBadge({ unlock }: { unlock: PlanUnlock | null }) {
  const label = unlock
    ? t("plans.shop.lockedIn", { plan: unlock.name })
    : t("plans.shop.lockedNone");
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      data-testid="plan-lock"
      className="ms-auto inline-flex size-5 flex-none items-center justify-center rounded-full ady-bg-brand-tint"
    >
      <Icon name={unlock?.icon ?? "lock"} gradient className="size-3" />
    </span>
  );
}

/** In place of a locked page: the plan that has the feature, what else it brings, and Upgrade. */
function FeatureLocked({ feature, unlock }: { feature: string; unlock: PlanUnlock | null }) {
  const { membership } = useShell();
  const orgId = membership.org.id;
  const canBuy = can(membership, ["billing.manage"]);
  const plans = useWalletPlans(orgId, canBuy && Boolean(unlock));
  const ent = useEntitlements(orgId);
  const [open, setOpen] = useState(false);
  const offer = plans.data?.plans.find((p) => p.id === unlock?.plan_id) ?? null;
  const name = featureLabel(feature);
  // Other features the shop gets with that plan.
  const also = offer
    ? PLAN_FEATURES.filter(
        (d) =>
          d.kind === "flag" &&
          d.key !== feature &&
          ent.data?.locks &&
          d.key in ent.data.locks &&
          offer.entitlements.some((e) => e.key === d.key && e.value !== 0),
      ).slice(0, 6)
    : [];
  const { orgSlug, storeSlug } = useShell();
  return (
    <section
      className="mx-auto flex max-w-xl flex-col items-center gap-3 rounded-md border border-card bg-surface p-6 text-center"
      data-testid="feature-locked"
    >
      <span className="flex size-12 items-center justify-center rounded-full ady-bg-brand-tint">
        <Icon name={unlock?.icon ?? "lock"} gradient />
      </span>
      <h1 className="text-title-sm font-semibold">
        {unlock
          ? t("plans.shop.lockedTitle", { feature: name, plan: unlock.name })
          : t("plans.shop.lockedTitleNone", { feature: name })}
      </h1>
      <p className="text-sm text-fg-secondary">
        {unlock
          ? t("plans.shop.lockedBody", { plan: unlock.name })
          : t("plans.shop.lockedBodyNone")}
      </p>
      {unlock?.monthly_paisa ? (
        <span className="rounded-full ady-bg-brand-tint px-3 py-0.5 text-xs font-semibold ady-text-brand">
          {t("plans.shop.from", { amount: formatMoney(unlock.monthly_paisa) })}
        </span>
      ) : null}
      {also.length ? (
        <div className="flex w-full flex-col gap-1.5 rounded-sm bg-sunken p-3 text-start">
          <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
            {t("plans.shop.alsoIncludes", { plan: unlock?.name ?? "" })}
          </span>
          <ul className="grid gap-1 text-xs sm:grid-cols-2">
            {also.map((d) => (
              <li key={d.key} className="flex items-center gap-1.5">
                <span className="text-success-fg">
                  <Icon name="check" />
                </span>
                {featureLabel(d.key)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap justify-center gap-2 pt-1">
        {unlock && canBuy && offer ? (
          <Button icon={unlock.icon} onClick={() => setOpen(true)} data-testid="feature-upgrade">
            {t("plans.shop.upgrade", { plan: unlock.name })}
          </Button>
        ) : null}
        <AppLink
          href={`/${orgSlug}/${storeSlug}/wallet?tab=plan`}
          className="inline-flex min-h-control items-center rounded-xs border border-control px-4 text-sm font-medium hover:bg-hover"
        >
          {t("plans.shop.seePlans")}
        </AppLink>
      </div>
      {plans.data ? (
        <CheckoutPopup
          orgId={orgId}
          plans={plans.data}
          plan={open ? offer : null}
          period="monthly"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

/** Wraps the page: a locked feature's page is replaced by <FeatureLocked> (the data stays). */
export function PlanGate({ children }: { children: ReactNode }) {
  const { orgSlug, storeSlug } = useShell();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lockFor = usePlanLocks();
  const pausedFor = usePausedNav();
  const item: NavItem | undefined = itemForPathname(pathname, orgSlug, storeSlug);
  // D-067: a feature Adyvance paused shows "paused" before the plan lock.
  const paused = item ? pausedFor(item.id) : null;
  if (paused) return <FeaturePaused feature={paused} />;
  const lock = item ? lockFor(item.id) : null;
  if (!lock) return <>{children}</>;
  return <FeatureLocked feature={lock.feature} unlock={lock.unlock} />;
}

// ---------- switches (D-067) ----------

/** The switch (feature key) that pauses a nav item for this shop, if any. */
export function usePausedNav() {
  const { membership } = useShell();
  const ent = useEntitlements(membership.org.id);
  return useMemo(() => {
    const paused = new Set(ent.data?.paused ?? []);
    return (navId: string): string | null =>
      paused.size
        ? (SWITCHES.find((s) => paused.has(s.key) && s.nav.includes(navId))?.key ?? null)
        : null;
  }, [ent.data]);
}

/** The small "Paused" chip on a sidebar row. */
export function PausedBadge() {
  return (
    <span
      className="ms-auto inline-flex flex-none items-center gap-0.5 rounded-full bg-warning-bg px-1.5 text-2xs font-semibold text-warning-fg"
      data-testid="paused-badge"
    >
      <Icon name="clock" className="size-3" />
      {t("platformOps.shop.pausedBadge")}
    </span>
  );
}

function FeaturePaused({ feature }: { feature: string }) {
  const { orgSlug, storeSlug } = useShell();
  return (
    <section
      className="mx-auto flex max-w-xl flex-col items-center gap-3 rounded-md border border-card bg-surface p-6 text-center"
      data-testid="feature-paused"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-warning-bg text-warning-fg">
        <Icon name="clock" />
      </span>
      <h1 className="text-title-sm font-semibold">
        {t("platformOps.shop.pausedTitle", { feature: featureLabel(feature) })}
      </h1>
      <p className="text-sm text-fg-secondary">{t("platformOps.shop.pausedBody")}</p>
      <AppLink
        href={`/${orgSlug}/${storeSlug}/settings/help`}
        className="text-sm font-medium text-link"
      >
        {t("platformOps.shop.pausedHelp")}
      </AppLink>
    </section>
  );
}

// ---------- banners ----------

export function useNotices(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.notices(orgId),
    queryFn: () => shopControlApi.notices(orgId),
    enabled,
    refetchInterval: 60_000,
  });
}

const TONE_KIND = {
  info: "info",
  success: "success",
  warning: "pending-activation",
  critical: "error",
} as const;

export function ShellBanners() {
  const { me, membership } = useShell();
  const orgId = membership.org.id;
  const qc = useQueryClient();
  const toast = useToast();
  const support = me.support;
  const canDecide = !support && can(membership, ["org.update"]);
  const grants = useQuery({
    queryKey: keys.grants(orgId),
    queryFn: () => shopControlApi.grants(orgId),
    enabled: !support,
    refetchInterval: 30_000,
  });
  const notices = useNotices(orgId, !support);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.grants(orgId) });
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: "approve" | "deny" }) =>
      shopControlApi.decide(orgId, v.id, v.decision),
    onSuccess: async (_d, v) => {
      await refresh();
      toast.show({
        tone: "success",
        title:
          v.decision === "approve" ? t("plans.shop.access.allowed") : t("plans.shop.access.denied"),
      });
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });
  const end = useMutation({
    mutationFn: (grantId: string) => shopControlApi.end(orgId, grantId),
    onSuccess: async () => {
      await refresh();
      toast.show({ tone: "success", title: t("plans.shop.access.endedOk") });
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => shopControlApi.seen(orgId, id, true),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notices(orgId) }),
  });
  const leave = async () => {
    if ((globalThis as { __ADY_MOCKS__?: boolean }).__ADY_MOCKS__ && import.meta.env.DEV) {
      const m = await import("../mocks/modules/platform");
      m.leaveSupportView();
      location.assign("/");
      return;
    }
    await api.logout().catch(() => undefined);
    location.assign("/sign-in");
  };

  const asked = (grants.data?.data ?? []).find((g) => g.state === "requested");
  const live = (grants.data?.data ?? []).find((g) => g.state === "approved");
  const pinned = (notices.data?.data ?? []).filter((n) => n.pinned);
  const banners: ReactNode[] = [];
  if (support)
    banners.push(
      <Banner
        key="support"
        kind="pending-activation"
        title={t("plans.shop.access.supportView", {
          shop: membership.org.name,
          time: formatTime(support.expires_at),
        })}
        action={{ label: t("plans.shop.access.leave"), onClick: () => void leave() }}
        data-testid="support-view-banner"
      />,
    );
  if (membership.suspended)
    banners.push(
      <Banner
        key="suspended"
        kind="error"
        title={t("plans.shop.suspended", { reason: membership.suspended.reason ?? "" })}
        data-testid="suspended-banner"
      />,
    );
  if (asked && canDecide)
    banners.push(
      <Banner
        key="asked"
        kind="info"
        title={t("plans.shop.access.askTitle")}
        data-testid="access-request-banner"
      >
        <p>
          {t("plans.shop.access.askBody", {
            staff: asked.staff_name,
            minutes: asked.minutes,
            reason: asked.reason,
          })}
        </p>
        <div className="flex gap-2 pt-1.5">
          <Button
            size="sm"
            icon="check"
            loading={decide.isPending && decide.variables?.decision === "approve"}
            onClick={() => decide.mutate({ id: asked.id, decision: "approve" })}
            data-testid="access-allow"
          >
            {t("plans.shop.access.allow")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={decide.isPending && decide.variables?.decision === "deny"}
            onClick={() => decide.mutate({ id: asked.id, decision: "deny" })}
          >
            {t("plans.shop.access.deny")}
          </Button>
        </div>
      </Banner>,
    );
  if (live && live.expires_at)
    banners.push(
      <Banner
        key="live"
        kind="pending-activation"
        title={t("plans.shop.access.liveTitle", { time: formatTime(live.expires_at) })}
        action={
          canDecide
            ? { label: t("plans.shop.access.end"), onClick: () => end.mutate(live.id) }
            : undefined
        }
        data-testid="access-live-banner"
      />,
    );
  for (const n of pinned)
    banners.push(
      <Banner
        key={n.id}
        kind={TONE_KIND[n.tone]}
        title={n.title}
        onDismiss={() => dismiss.mutate(n.id)}
        data-testid="notice-banner"
      >
        <p className="whitespace-pre-line">{n.body}</p>
        {n.link ? <NoticeLink link={n.link} /> : null}
      </Banner>,
    );
  if (!banners.length) return null;
  return <div className="mb-3 flex flex-col gap-2">{banners}</div>;
}

export function NoticeLink({
  link,
  className = "text-sm font-medium text-link",
}: {
  link: string;
  className?: string;
}) {
  const { orgSlug, storeSlug } = useShell();
  const external = link.startsWith("https://");
  const href = external ? link : `/${orgSlug}/${storeSlug}${link}`;
  return external ? (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {t("plans.shop.notices.open")}
    </a>
  ) : (
    <AppLink href={href} className={className}>
      {t("plans.shop.notices.open")}
    </AppLink>
  );
}

// ---------- bell ----------

/** Unseen messages from Adyvance (added to the bell's badge). */
export function useUnseenNotices(): number {
  const { me, membership } = useShell();
  const q = useNotices(membership.org.id, !me.support);
  return (q.data?.data ?? []).filter((n) => !n.seen).length;
}

/** "From Adyvance" at the top of the bell's dropdown; opening it marks them seen. `compact`: a title
 * and two lines of the message (the full text on hover). */
export function AdyvanceNotices({ open, compact }: { open: boolean; compact?: boolean }) {
  const { me, membership } = useShell();
  const orgId = membership.org.id;
  const qc = useQueryClient();
  const q = useNotices(orgId, !me.support);
  const list = (q.data?.data ?? []).slice(0, 5);
  const seen = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => shopControlApi.seen(orgId, id, false)));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notices(orgId) }),
  });
  const unseen = list
    .filter((n) => !n.seen)
    .map((n) => n.id)
    .join(",");
  const { mutate } = seen;
  useEffect(() => {
    if (open && unseen) mutate(unseen.split(","));
  }, [open, unseen, mutate]);
  if (!list.length) return null;
  if (compact)
    return (
      <section className="flex flex-col gap-1" data-testid="adyvance-notices">
        <h3 className="px-2 text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
          {t("plans.shop.notices.title")}
        </h3>
        <ul className="flex flex-col">
          {list.slice(0, 3).map((n) => (
            <li
              key={n.id}
              title={n.body}
              className={cx("flex flex-col rounded-sm px-2 py-1", !n.seen && "ady-bg-brand-tint")}
            >
              <span className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-xs font-semibold">{n.title}</span>
                {n.link ? (
                  <NoticeLink
                    link={n.link}
                    className="flex-none text-2xs font-semibold text-link"
                  />
                ) : (
                  <span className="flex-none text-2xs text-fg-tertiary">
                    {n.created_at ? formatDate(n.created_at) : ""}
                  </span>
                )}
              </span>
              <span className="line-clamp-2 text-2xs text-fg-secondary">{n.body}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  return (
    <section className="flex flex-col gap-1.5" data-testid="adyvance-notices">
      <h3 className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
        {t("plans.shop.notices.title")}
      </h3>
      <ul className="flex flex-col gap-1">
        {list.map((n) => (
          <li
            key={n.id}
            className={cx(
              "flex flex-col gap-0.5 rounded-sm border border-card px-3 py-2",
              !n.seen && "ady-bg-brand-tint",
            )}
          >
            <span className="text-xs font-semibold">{n.title}</span>
            <span className="text-xs whitespace-pre-line text-fg-secondary">{n.body}</span>
            <span className="text-2xs text-fg-tertiary">
              {n.created_at ? formatDate(n.created_at) : ""}
            </span>
            {n.link ? <NoticeLink link={n.link} /> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
